'use client'

import { type CSSProperties, useEffect, useId, useMemo, useRef, useState } from 'react'
import Link, { useCategoryHref } from './category-link'
import { usePathname } from 'next/navigation'
import { ChevronDown, ChevronRight, Layers3, Search, X } from 'lucide-react'
import type { CategoryNavigationNode } from '@/lib/categories/category-navigation'
import { cn } from '@/lib/utils'
import styles from './category-tree-sidebar.module.scss'
import NodeImage from './category-node-image'
import CategoryPublicDescription from './category-public-description'
import {
  findCategoryEditorialFields,
  findCategoryPublicDescription,
} from '@/lib/categories/category-description-selection'
import { CategoryTreeFooter, CategoryTreeFreeToggle, CategoryTreeSort } from './filters/category-tree-controls'
import {
  matchesGridNavigationDestination,
  useGridNavigationLoadingDestination,
} from '@/lib/hooks/use-grid-navigation-loading'

const EMPTY_EXPANDED: Record<string, boolean> = {}

function filterNodes(nodes: CategoryNavigationNode[], query: string): CategoryNavigationNode[] {
  return nodes.flatMap((node) => {
    if (node.name.toLowerCase().includes(query)) return [node]
    const children = filterNodes(node.children, query)
    return children.length ? [{ ...node, children }] : []
  })
}

type BranchProps = {
  nodes: CategoryNavigationNode[]
  pathname: string
  expanded: Record<string, boolean>
  searching: boolean
  onToggle: (href: string, open: boolean, trigger: HTMLButtonElement) => void
  onNavigate: () => void
  loadingDestination: string | null
  categoryHref: (href: string) => string
  depth?: number
}

function CategoryBranch({
  nodes,
  pathname,
  expanded,
  searching,
  onToggle,
  onNavigate,
  loadingDestination,
  categoryHref,
  depth = 0,
}: BranchProps) {
  const branchId = useId()

  return (
    <ul className={cn(styles.branch, depth > 0 && styles.nested)}>
      {nodes.map((node, index) => {
        const selected = pathname === node.href || pathname === node.destinationHref
        const ancestor = !selected && pathname.startsWith(`${node.href}/`)
        const hasChildren = node.children.length > 0
        const open = expanded[node.href] ?? (searching || selected || ancestor)
        const childrenId = `${branchId}-${index}`
        const loading = matchesGridNavigationDestination(
          categoryHref(node.destinationHref ?? node.href),
          loadingDestination
        )

        return (
          <li
            key={node.href}
            className={selected && !hasChildren ? styles.stickySelection : undefined}
            data-category-sticky-selection={selected && !hasChildren ? '' : undefined}
            style={{ '--category-tree-level': depth } as CSSProperties}
          >
            <div
              className={cn(
                styles.row,
                selected && styles.selected,
                ancestor && styles.ancestor,
                hasChildren && open && styles.stickyParent
              )}
              data-category-sticky-parent={hasChildren && open ? '' : undefined}
            >
              {hasChildren ? (
                <button
                  type="button"
                  className={styles.disclosure}
                  aria-label={`${open ? 'Collapse' : 'Expand'} ${node.name}`}
                  aria-expanded={open}
                  aria-controls={childrenId}
                  onClick={(event) => onToggle(node.href, !open, event.currentTarget)}
                >
                  {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                </button>
              ) : (
                <span className={styles.leaf} aria-hidden="true" />
              )}
              <Link
                href={node.destinationHref ?? node.href}
                animateTransition={depth > 1}
                prefetch={false}
                aria-current={selected ? 'page' : undefined}
                aria-busy={loading}
                className={styles.nodeLink}
                onClick={onNavigate}
              >
                <NodeImage key={node.image ?? 'fallback'} image={node.image} loading={loading} />
                <span className={styles.nodeName}>{node.name}</span>
              </Link>
            </div>
            {hasChildren && (
              <div id={childrenId} hidden={!open}>
                {open && (
                  <CategoryBranch
                    nodes={node.children}
                    pathname={pathname}
                    expanded={expanded}
                    searching={searching}
                    onToggle={onToggle}
                    onNavigate={onNavigate}
                    loadingDestination={loadingDestination}
                    categoryHref={categoryHref}
                    depth={depth + 1}
                  />
                )}
              </div>
            )}
          </li>
        )
      })}
    </ul>
  )
}

export default function CategoryTreeSidebar({
  nodes,
  mainCategorySlug,
}: {
  nodes: CategoryNavigationNode[]
  mainCategorySlug?: string | null
}) {
  const routePathname = usePathname().replace(/\/$/, '')
  const pathname = mainCategorySlug ? `/${mainCategorySlug}` : routePathname
  const categoryHref = useCategoryHref()
  const loadingDestination = useGridNavigationLoadingDestination()
  const searchId = useId()
  const listRef = useRef<HTMLDivElement>(null)
  const preloadedImages = useRef<HTMLImageElement[]>([])
  const [query, setQuery] = useState('')
  const [mobileOpen, setMobileOpen] = useState(false)
  const [branches, setBranches] = useState<{ pathname: string; expanded: Record<string, boolean> }>({
    pathname,
    expanded: {},
  })
  const description = findCategoryPublicDescription(nodes, pathname)
  const editorial = findCategoryEditorialFields(nodes, pathname)
  const normalizedQuery = query.trim().toLowerCase()
  const visibleNodes = useMemo(
    () => (normalizedQuery ? filterNodes(nodes, normalizedQuery) : nodes),
    [nodes, normalizedQuery]
  )
  // A route change reveals its ancestors, including browser Back and carousel navigation.
  const expanded = branches.pathname === pathname ? branches.expanded : EMPTY_EXPANDED

  useEffect(() => {
    const sources = new Set<string>()
    const collect = (branch: CategoryNavigationNode[]) => {
      for (const node of branch) {
        if (node.image?.startsWith('/category-thumbnails/')) sources.add(node.image)
        collect(node.children)
      }
    }
    collect(nodes)
    const pending = Array.from(sources)
    let cancelled = false

    const preload = async () => {
      while (!cancelled && pending.length) {
        const source = pending.shift()
        if (!source) return
        const image = new window.Image()
        image.fetchPriority = 'low'
        image.decoding = 'async'
        image.src = source
        preloadedImages.current.push(image)
        await image.decode().catch(() => undefined)
      }
    }

    // Warm every branch without mounting hidden rows or competing with the asset grid.
    void Promise.all(Array.from({ length: 6 }, preload))
    return () => {
      cancelled = true
      preloadedImages.current = []
    }
  }, [nodes])

  useEffect(() => {
    const list = listRef.current
    if (!list) return
    const rows = Array.from(list.querySelectorAll<HTMLElement>('[data-category-sticky-parent]'))
    const selection = list.querySelector<HTMLElement>('[data-category-sticky-selection]')
    const selectionGroup = selection?.parentElement

    const updateOffsets = () => {
      // Read geometry together; scrolling itself stays entirely in CSS.
      const measurements = rows.map((row) => ({
        row,
        height: row.getBoundingClientRect().height + parseFloat(getComputedStyle(row).marginBottom),
      }))
      const selectionRow = selection?.firstElementChild
      const selectionHeight = selection
        ? selection.getBoundingClientRect().height +
          (selectionRow ? parseFloat(getComputedStyle(selectionRow).marginBottom) : 0)
        : 0
      const offsets = new Map<Element, number>()
      const updates: Array<{ children: HTMLElement; offset: number }> = []
      for (const { row, height } of measurements) {
        const item = row.parentElement
        const parentItem = item?.parentElement?.closest('li')
        const offset = (parentItem ? offsets.get(parentItem) : 0) ?? 0
        const children = row.nextElementSibling
        if (item) offsets.set(item, offset + height)
        if (children instanceof HTMLElement) updates.push({ children, offset: offset + height })
      }
      for (const { children, offset } of updates) {
        // Descendants inherit their ancestors' combined heights, including wrapped names.
        children.style.setProperty('--category-tree-sticky-top', `${offset}px`)
      }
      selectionGroup?.style.setProperty('--category-tree-selection-height', `${selectionHeight}px`)
    }

    updateOffsets()
    const observer = new ResizeObserver(updateOffsets)
    rows.forEach((row) => observer.observe(row))
    if (selection) observer.observe(selection)
    return () => {
      observer.disconnect()
      selectionGroup?.style.removeProperty('--category-tree-selection-height')
    }
  }, [visibleNodes, expanded, pathname])

  useEffect(() => {
    if (normalizedQuery) return
    const list = listRef.current
    const selected = list?.querySelector<HTMLElement>('[aria-current="page"]')
    if (!list || !selected) return
    const listBounds = list.getBoundingClientRect()
    const selectedBounds = selected.getBoundingClientRect()
    const stickyOffset = parseFloat(getComputedStyle(selected).getPropertyValue('--category-tree-sticky-top')) || 0
    if (selectedBounds.top < listBounds.top + stickyOffset || selectedBounds.bottom > listBounds.bottom) {
      const availableHeight = Math.max(0, list.clientHeight - stickyOffset - selectedBounds.height)
      list.scrollTop += selectedBounds.top - listBounds.top - stickyOffset - availableHeight / 3
    }
  }, [pathname, normalizedQuery])

  const changeQuery = (value: string) => {
    setQuery(value)
    setBranches({ pathname, expanded: {} })
  }

  return (
    <aside
      className={cn(styles.column, mobileOpen && styles.mobileOpen)}
      data-slot="category-tree-sidebar"
      aria-label="Category navigation"
    >
      <button
        type="button"
        className={styles.mobileTrigger}
        aria-expanded={mobileOpen}
        aria-controls={`${searchId}-panel`}
        onClick={() => setMobileOpen((open) => !open)}
      >
        <Layers3 size={16} aria-hidden="true" />
        <span>Categories</span>
      </button>
      <div className={styles.panel} id={`${searchId}-panel`}>
        <div className={styles.search}>
          <div hidden>
            <Search size={15} aria-hidden="true" />
            <label htmlFor={searchId} className="tw-sr-only">
              Find a category
            </label>
            <input
              id={searchId}
              type="search"
              placeholder="Find a category…"
              value={query}
              onChange={(event) => changeQuery(event.target.value)}
            />
            {query && (
              <button
                className={styles.clearSearch}
                type="button"
                aria-label="Clear category search"
                onClick={() => changeQuery('')}
              >
                <X size={14} />
              </button>
            )}
          </div>
          <CategoryTreeFreeToggle />
          <CategoryTreeSort />
          <button
            type="button"
            className={styles.mobileClose}
            aria-label="Close categories"
            onClick={() => setMobileOpen(false)}
          >
            <X size={16} />
          </button>
        </div>
        <div className={styles.body}>
          <div className={styles.scroll} ref={listRef}>
            <nav aria-label="Asset categories">
              <CategoryBranch
                nodes={visibleNodes}
                pathname={pathname}
                expanded={expanded}
                searching={!!normalizedQuery}
                onToggle={(href, value, trigger) => {
                  const list = listRef.current
                  const item = trigger.closest('li')
                  if (!value && list && item) {
                    // Keep a collapsed pinned parent in view when its sticky positioning is removed.
                    const offset =
                      parseFloat(getComputedStyle(trigger).getPropertyValue('--category-tree-sticky-top')) || 0
                    const delta = item.getBoundingClientRect().top - list.getBoundingClientRect().top - offset
                    if (delta < 0) list.scrollTop += delta
                  }
                  setBranches({ pathname, expanded: { ...expanded, [href]: value } })
                }}
                onNavigate={() => {
                  setQuery('')
                  setMobileOpen(false)
                }}
                loadingDestination={loadingDestination}
                categoryHref={categoryHref}
              />
            </nav>
            {!visibleNodes.length && (
              <p className={styles.empty} role="status">
                No categories found.
              </p>
            )}
          </div>
          <CategoryPublicDescription description={description} quickTip={editorial.quickTip} />
        </div>
        <CategoryTreeFooter key={pathname} mainCategorySlug={mainCategorySlug} fields={editorial} />
      </div>
    </aside>
  )
}
